<template>
  <div class="p-4">
    <div v-if="!periods.length" class="text-center py-5 text-muted">
      <i class="ri-history-line" style="font-size: 2.5rem;"></i>
      <p class="mt-2 mb-0">Sin historial laboral registrado.</p>
    </div>

    <div v-else>
      <div class="flex justify-end mb-2">
        <wz-columns-preferences-panel
          :columns="columns"
          :hidden-columns="hiddenColumns"
          @toggle="toggleColumn"
          @reorder="reorderColumns"
          @reset="resetColumns"
        />
      </div>
      <div class="overflow-x-auto">
      <table class="wz-table w-full">
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              :class="[{
                'wz-th-sortable': col.sortKey,
                'is-sorted': col.sortKey && sortKey === col.sortKey,
              }]"
              @click="col.sortKey ? toggleSort(col.sortKey) : null"
            >
              {{ col.label }}
              <i v-if="col.sortKey" :class="['wz-th-sort-icon', sortIcon(col.sortKey)]"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in sortedList(rows)" :key="p.id">
            <td v-for="col in visibleColumns" :key="col.key">
              <template v-if="col.key === 'company'">{{ p.companyName || '—' }}</template>
              <template v-else-if="col.key === 'hire'"><span v-local-date="p.hireDate"></span></template>
              <template v-else-if="col.key === 'cessation'"><span v-local-date="p.cessationDate"></span></template>
              <template v-else-if="col.key === 'reason'">{{ p.cessationReasonName || '---' }}</template>
              <template v-else-if="col.key === 'status'">
                <wz-badge :variant="statusVariant(p)" pill>
                  {{ statusLabel(p) }}
                </wz-badge>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  </div>
</template>

<script>
import { SortMixin, draggableColumnsMixin } from '@wizepe/wizeui'
import ColumnPreferencesService from '@/core/services/ColumnPreferencesService'

// Orden por defecto de las columnas. Ninguna es fija (la tabla es de solo lectura, sin "Opciones").
function defaultColumns() {
  return [
    { key: 'company',   label: 'Empresa',          sortKey: 'companyName',   fixed: false },
    { key: 'hire',      label: 'Fecha de ingreso', sortKey: 'hireDate',      fixed: false },
    { key: 'cessation', label: 'Fecha de cese',    sortKey: 'cessationDate', fixed: false },
    { key: 'reason',    label: 'Motivo de baja',   sortKey: null,            fixed: false },
    { key: 'status',    label: 'Estado',           sortKey: '_statusOrder',  fixed: false },
  ]
}

export default {
  name: 'EmploymentHistoryTab',
  mixins: [SortMixin, draggableColumnsMixin],
  props: {
    periods: { type: Array, default: () => [] },
    // Servicio de preferencias por usuario (backend). Override del prop del mixin con default.
    preferencesService: { type: Object, default: () => ColumnPreferencesService },
  },
  data() {
    return {
      columnsStorageKey: 'crystal_employment_history_columns_order',
      columnsListKey: 'employment_history',
    }
  },
  computed: {
    // Enriquece cada período con una clave numérica de prioridad para el sort de "Estado":
    // VIGENTE (0) > VENCIDO (1) > CERRADO (2). Empresa/fechas ordenan por su valor natural
    // (las fechas ISO 'yyyy-MM-dd' ordenan cronológicamente como string; los nulls quedan al final).
    rows() {
      const priority = { VIGENTE: 0, VENCIDO: 1, CERRADO: 2 }
      return this.periods.map(p => {
        const ord = priority[this.periodStatus(p)]
        return { ...p, _statusOrder: ord !== undefined ? ord : 99 }
      })
    },
  },
  created() {
    // Orden de columnas persistido (localStorage) sobre el default; el drag/persistencia vienen del mixin.
    this.initColumns(defaultColumns(), this.columnsStorageKey, this.columnsListKey)
    // Sort inicial: fecha de ingreso descendente (más reciente primero), lo natural para un historial.
    this.sortKey = 'hireDate'
    this.sortDir = 'desc'
  },
  methods: {
    // displayStatus lo calcula el backend date-aware (VIGENTE / CERRADO / VENCIDO); siempre viene en
    // el payload de getHistory. Fallback defensivo a VIGENTE por si faltara.
    periodStatus(p) {
      return p.displayStatus || 'VIGENTE'
    },
    statusLabel(p) {
      return this.periodStatus(p)
    },
    statusVariant(p) {
      const map = { VIGENTE: 'success-solid', VENCIDO: 'warning-solid', CERRADO: 'secondary-solid' }
      return map[this.periodStatus(p)] || 'secondary-solid'
    },
  },
}
</script>

<style lang="stylus" scoped>
.col-draggable
  cursor move
</style>
